import { otpsSend, otpsVerify } from '@zealot/shared/apiClient';
import { getApiErrorMessage } from '@zealot/shared/errors';
import {
  Button,
  Form,
  FormField,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  REGEXP_ONLY_DIGITS,
  Stack,
  Text,
  useZodForm,
} from '@zealot/ui';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useCooldownRemaining } from './useCooldownRemaining';

const CODE_LENGTH = 6;

const verifyCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Please enter the 6-digit code'),
});

type VerifyCodeValues = z.infer<typeof verifyCodeSchema>;

type ApiError = { code?: string; message?: string } | undefined;

type OtpEnterStepProps = {
  email: string;
  primaryColor: string;
  onVerified: (token: string) => void;
  onError: (message: string) => void;
  lastSentAt: number | null;
  onResent?: () => void;
  cooldownSeconds?: number;
};

const isFatalVerifyError = (message: string) => message.includes('expired') || message.includes('max_attempts');

export const OtpEnterStep = ({
  email,
  primaryColor,
  onVerified,
  onError,
  lastSentAt,
  onResent,
  cooldownSeconds = 60,
}: OtpEnterStepProps) => {
  const client = useApiClient();
  const form = useZodForm({ schema: verifyCodeSchema, defaultValues: { code: '' }, mode: 'onSubmit' });
  const resendCooldown = useCooldownRemaining(lastSentAt, cooldownSeconds);
  const [isResending, setIsResending] = useState(false);
  const isBusy = form.formState.isSubmitting || isResending;
  const code = form.watch('code');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCodeError = (message: string) => {
    if (isFatalVerifyError(message)) {
      onError(message);
      return;
    }
    form.setError('code', { message });
  };

  const handleValidSubmit = async ({ code: validCode }: VerifyCodeValues) => {
    try {
      const response = await otpsVerify({ client, body: { email, code: validCode } });
      if (!isMounted.current) return;

      if (response.error || (response.response?.status ?? 0) >= 400) {
        const apiError = response.error as ApiError;
        if (apiError?.code === 'USER_BANNED') {
          onError(getApiErrorMessage(apiError, 'This account has been banned.'));
          return;
        }
        handleCodeError(getApiErrorMessage(apiError, 'Invalid code'));
        return;
      }

      const token = response.data?.data?.token;
      if (!token) {
        form.setError('code', { message: 'No authentication token received' });
        return;
      }

      onVerified(token);
    } catch (err) {
      if (!isMounted.current) return;
      handleCodeError(getApiErrorMessage(err, 'Invalid code'));
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    try {
      const response = await otpsSend({ client, body: { email } });
      if (!isMounted.current) return;

      if (response.error || (response.response?.status ?? 0) >= 400) {
        const apiError = response.error as ApiError;
        if (apiError?.code === 'USER_BANNED') {
          onError(getApiErrorMessage(apiError, 'This account has been banned.'));
          return;
        }
        form.setError('code', { message: getApiErrorMessage(apiError, 'Failed to resend') });
        return;
      }

      form.reset({ code: '' });
      onResent?.();
    } catch (err) {
      if (!isMounted.current) return;
      form.setError('code', { message: getApiErrorMessage(err, 'Failed to resend') });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Stack gap={6}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleValidSubmit)} noValidate>
          <Stack gap={6}>
            <FormField
              control={form.control}
              name="code"
              label="Verification code"
              render={({ field }) => (
                <InputOTP
                  size="lg"
                  maxLength={CODE_LENGTH}
                  pattern={REGEXP_ONLY_DIGITS}
                  disabled={isBusy}
                  autoFocus
                  {...field}
                >
                  <InputOTPGroup>
                    {Array.from({ length: CODE_LENGTH }, (_, index) => (
                      <InputOTPSlot key={index} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              style={{ backgroundColor: primaryColor }}
              disabled={isBusy || code.length !== CODE_LENGTH}
            >
              {form.formState.isSubmitting ? 'Verifying...' : 'Verify'}
            </Button>
          </Stack>
        </form>
      </Form>

      <Text size="sm" tone="muted" className="text-center">
        Didn&apos;t receive a code?{' '}
        {resendCooldown > 0 ? (
          `Resend in ${resendCooldown}s`
        ) : (
          <Button variant="link" size="sm" className="h-auto p-0 text-sm" onClick={handleResend} disabled={isBusy}>
            Resend code
          </Button>
        )}
      </Text>
    </Stack>
  );
};
